import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, RotateCcw } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix for default marker icons in React-Leaflet
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

// Continental US bounds to prevent over-zooming
const US_BOUNDS = {
  maxLat: 49,    // Canada border
  minLat: 24,    // Southern Florida/Texas
  maxLng: -66,   // Atlantic coast
  minLng: -125   // Pacific coast
};

interface Show {
  id: number;
  date: string;
  venue: {
    id: number;
    name: string;
    city?: string | null;
    state?: string | null;
    lat?: string | number | null;
    lng?: string | number | null;
  };
  artist?: {
    id: number;
    name: string;
  } | null;
}

interface ShowPackage {
  showId: number;
  setCore: boolean;
  setAudio: boolean;
  setVideo: boolean;
}

interface PlanMapViewProps {
  shows: Show[];
  showPackages: Record<number, ShowPackage>;
}

// Create color-coded marker icons for different package types with pulse animation
const createColoredIcon = (color: string, packageType: string) => {
  const svgIcon = `
    <div style="position: relative; width: 25px; height: 41px;">
      <style>
        @keyframes pulse-${packageType} {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.3); opacity: 0.2; }
        }
        .pulse-ring-${packageType} {
          position: absolute;
          width: 25px;
          height: 25px;
          border-radius: 50%;
          background: ${color};
          top: 0;
          left: 0;
          animation: pulse-${packageType} 2s ease-in-out infinite;
        }
      </style>
      <div class="pulse-ring-${packageType}"></div>
      <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg" style="position: absolute; top: 0; left: 0;">
        <path fill="${color}" stroke="white" stroke-width="2" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.4 12.5 28.5 12.5 28.5S25 20.9 25 12.5C25 5.6 19.4 0 12.5 0z"/>
        <circle cx="12.5" cy="12.5" r="6" fill="white"/>
      </svg>
    </div>
  `;

  return L.divIcon({
    html: svgIcon,
    className: 'custom-marker',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [0, -41],
  });
};

const setCoreIcon = createColoredIcon('#6B7280', 'setcore'); // Gray for SET Core
const setAudioIcon = createColoredIcon('#3B82F6', 'setaudio'); // Blue for SET Audio
const setVideoIcon = createColoredIcon('#8B5CF6', 'setvideo'); // Purple for SET Video
const mixedIcon = createColoredIcon('#10B981', 'mixed'); // Green for mixed packages

export function PlanMapView({ shows, showPackages }: PlanMapViewProps) {
  // Filter shows that have valid coordinates
  const showsWithCoords = useMemo(() => {
    return shows.filter(show => {
      const lat = show.venue.lat ? Number(show.venue.lat) : null;
      const lng = show.venue.lng ? Number(show.venue.lng) : null;
      return lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng);
    });
  }, [shows]);

  // Calculate bounds to auto-zoom the map
  const bounds = useMemo(() => {
    if (showsWithCoords.length === 0) return null;

    const coords = showsWithCoords.map(show => ({
      lat: Number(show.venue.lat),
      lng: Number(show.venue.lng),
    }));

    const latitudes = coords.map(c => c.lat);
    const longitudes = coords.map(c => c.lng);

    // Constrain bounds to Continental US to prevent over-zooming
    return {
      minLat: Math.max(Math.min(...latitudes), US_BOUNDS.minLat),
      maxLat: Math.min(Math.max(...latitudes), US_BOUNDS.maxLat),
      minLng: Math.max(Math.min(...longitudes), US_BOUNDS.minLng),
      maxLng: Math.min(Math.max(...longitudes), US_BOUNDS.maxLng),
    };
  }, [showsWithCoords]);

  // Calculate center and zoom
  const mapCenter = useMemo((): [number, number] => {
    if (!bounds) return [39.8283, -98.5795]; // Center of US as default

    return [
      (bounds.minLat + bounds.maxLat) / 2,
      (bounds.minLng + bounds.maxLng) / 2,
    ];
  }, [bounds]);

  // Determine which icon to use based on packages
  const getMarkerIcon = (showId: number) => {
    const pkg = showPackages[showId];
    if (!pkg) return DefaultIcon;

    const activePackages = [pkg.setCore, pkg.setAudio, pkg.setVideo].filter(Boolean).length;

    if (activePackages > 1) return mixedIcon;
    if (pkg.setVideo) return setVideoIcon;
    if (pkg.setAudio) return setAudioIcon;
    if (pkg.setCore) return setCoreIcon;

    return DefaultIcon;
  };

  // Get package label for tooltip
  const getPackageLabel = (showId: number): string => {
    const pkg = showPackages[showId];
    if (!pkg) return "No package";

    const packages: string[] = [];
    if (pkg.setCore) packages.push("SET Core");
    if (pkg.setAudio) packages.push("SET Audio");
    if (pkg.setVideo) packages.push("SET Video");

    return packages.length > 0 ? packages.join(" + ") : "No package";
  };

  if (showsWithCoords.length === 0) {
    return (
      <Card className="border-pink-200/50 overflow-hidden flex flex-col h-full shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Show Locations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
            <MapPin className="h-12 w-12 mb-2 opacity-20" />
            <p>No shows with location data</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-pink-200/50 overflow-hidden flex flex-col h-full shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Show Locations
          <span className="text-sm font-normal text-muted-foreground ml-2">
            ({showsWithCoords.length} {showsWithCoords.length === 1 ? 'show' : 'shows'})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[400px] rounded-xl overflow-hidden border-2 border-transparent bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 p-[0.5px] shadow-lg">
          <div className="h-full rounded-lg overflow-hidden bg-white">
            <MapContainer
              center={mapCenter}
              zoom={4}
              style={{ height: "100%", width: "100%" }}
              className="z-0"
              attributionControl={false}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              />

            {/* Auto-fit map to show bounds */}
            <MapBoundsFitter bounds={bounds} />

            {/* Reset view button */}
            <MapResetButton bounds={bounds} />

            {showsWithCoords.map((show) => {
              const lat = Number(show.venue.lat);
              const lng = Number(show.venue.lng);

              return (
                <Marker
                  key={show.id}
                  position={[lat, lng]}
                  icon={getMarkerIcon(show.id)}
                >
                  <Popup>
                    <div className="min-w-[220px]">
                      {/* Gradient Header */}
                      <div className="bg-gradient-to-r from-pink-500 to-blue-500 text-white px-3 py-2 -mx-3 -mt-3 mb-3 rounded-t">
                        <div className="font-bold text-base">
                          {show.artist?.name || "Unknown Artist"}
                        </div>
                      </div>

                      {/* Venue Info */}
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {show.venue.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {show.venue.city}, {show.venue.state}
                            </div>
                          </div>
                        </div>

                        {/* Date */}
                        <div className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                          {new Date(show.date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>

                        {/* Package Badge */}
                        <div className="inline-block px-3 py-1 rounded-full text-xs font-semibold text-white bg-gradient-to-r from-purple-500 to-blue-500">
                          {getPackageLabel(show.id)}
                        </div>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            </MapContainer>
          </div>
        </div>

        {/* Enhanced Interactive Legend */}
        <div className="flex flex-wrap gap-3 mt-4">
          {(() => {
            // Count shows by package type
            const packageCounts = { setCore: 0, setAudio: 0, setVideo: 0 };
            Object.values(showPackages).forEach((pkg) => {
              if (pkg.setCore) packageCounts.setCore++;
              if (pkg.setAudio) packageCounts.setAudio++;
              if (pkg.setVideo) packageCounts.setVideo++;
            });

            return (
              <>
                {packageCounts.setCore > 0 && (
                  <div className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 hover:shadow-md transition-all duration-200 hover:scale-105 cursor-default">
                    <div className="w-3 h-3 rounded-full bg-gray-500 group-hover:scale-110 transition-transform duration-200"></div>
                    <span className="text-sm font-medium text-gray-700">SET Core</span>
                    <span className="ml-1 px-2 py-0.5 text-xs font-bold text-white bg-gray-500 rounded-full">
                      {packageCounts.setCore}
                    </span>
                  </div>
                )}
                {packageCounts.setAudio > 0 && (
                  <div className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 hover:shadow-md transition-all duration-200 hover:scale-105 cursor-default">
                    <div className="w-3 h-3 rounded-full bg-blue-500 group-hover:scale-110 transition-transform duration-200"></div>
                    <span className="text-sm font-medium text-blue-700">SET Audio</span>
                    <span className="ml-1 px-2 py-0.5 text-xs font-bold text-white bg-blue-500 rounded-full">
                      {packageCounts.setAudio}
                    </span>
                  </div>
                )}
                {packageCounts.setVideo > 0 && (
                  <div className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-50 hover:bg-purple-100 hover:shadow-md transition-all duration-200 hover:scale-105 cursor-default">
                    <div className="w-3 h-3 rounded-full bg-purple-500 group-hover:scale-110 transition-transform duration-200"></div>
                    <span className="text-sm font-medium text-purple-700">SET Video</span>
                    <span className="ml-1 px-2 py-0.5 text-xs font-bold text-white bg-purple-500 rounded-full">
                      {packageCounts.setVideo}
                    </span>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </CardContent>
    </Card>
  );
}

// Component to handle map bounds fitting after mount
function MapBoundsFitter({ bounds }: { bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number } | null }) {
  const map = useMap();

  useEffect(() => {
    if (bounds && map) {
      const leafletBounds = L.latLngBounds(
        [bounds.minLat, bounds.minLng],
        [bounds.maxLat, bounds.maxLng]
      );

      // Use less padding for cross-country spans to keep map tighter
      const lngWidth = bounds.maxLng - bounds.minLng;
      const padding: [number, number] = lngWidth > 40 ? [20, 20] : [50, 50];

      map.fitBounds(leafletBounds, { padding });
    }
  }, [bounds, map]);

  return null;
}

// Component for reset view button
function MapResetButton({ bounds }: { bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number } | null }) {
  const map = useMap();

  const handleReset = () => {
    if (bounds && map) {
      const leafletBounds = L.latLngBounds(
        [bounds.minLat, bounds.minLng],
        [bounds.maxLat, bounds.maxLng]
      );

      // Use same dynamic padding as MapBoundsFitter
      const lngWidth = bounds.maxLng - bounds.minLng;
      const padding: [number, number] = lngWidth > 40 ? [20, 20] : [50, 50];

      map.fitBounds(leafletBounds, { padding });
    }
  };

  return (
    <div className="leaflet-top leaflet-right" style={{ pointerEvents: 'none' }}>
      <div className="leaflet-control" style={{ pointerEvents: 'auto', marginTop: '10px', marginRight: '10px' }}>
        <Button
          onClick={handleReset}
          size="sm"
          variant="secondary"
          className="shadow-md hover:shadow-lg transition-shadow gap-1"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset View
        </Button>
      </div>
    </div>
  );
}
